import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { stocktakingSettingsService, costingPolicyService, settingsService } from '../services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Settings, Save, Loader2, ArrowLeft, Scale, Info, FlaskConical } from 'lucide-react';
import { getApiError } from '@/lib/utils';

export default function StocktakingOrgSettingsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    default_vat_rate: '20',
    invoice_basis: 'net',
    sales_price_basis: 'gross',
    postmix_conversion_factor_ml: '1000',
  });
  const [costingPolicy, setCostingPolicy] = useState({
    costing_method: 'wac',
    allow_reference_cost_fallback: false,
  });
  const [methods, setMethods] = useState([]);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [methodology, setMethodology] = useState('legacy');
  const [savingMethodology, setSavingMethodology] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [data, globalPolicy, methodsData, orgSettings] = await Promise.all([
          stocktakingSettingsService.get(),
          costingPolicyService.getGlobal(),
          costingPolicyService.getMethods(),
          settingsService.get().catch(() => null),
        ]);
        setForm({
          default_vat_rate: data.default_vat_rate !== null && data.default_vat_rate !== undefined
            ? String(data.default_vat_rate) : '20',
          invoice_basis: data.invoice_basis || 'net',
          sales_price_basis: data.sales_price_basis || 'gross',
          postmix_conversion_factor_ml: data.postmix_conversion_factor_ml !== null && data.postmix_conversion_factor_ml !== undefined
            ? String(data.postmix_conversion_factor_ml) : '1000',
        });
        setCostingPolicy({
          costing_method: globalPolicy.costing_method || 'wac',
          allow_reference_cost_fallback: globalPolicy.allow_reference_cost_fallback || false,
        });
        setMethods(methodsData.methods || []);
        setMethodology(orgSettings?.headline_methodology || 'legacy');
      } catch {
        // Use defaults on error
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSaveMethodology = async (newValue) => {
    setSavingMethodology(true);
    try {
      const res = await settingsService.update({ headline_methodology: newValue });
      setMethodology(res.headline_methodology || newValue);
      toast({
        title: 'Methodology updated',
        description: newValue === 'revised'
          ? 'Headline now uses Actual Receipts − Estimated Receipts. Recalculate sessions to apply.'
          : 'Headline now uses the legacy PLU-led stocktake result.',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to update methodology'),
        variant: 'destructive',
      });
    } finally {
      setSavingMethodology(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await stocktakingSettingsService.save({
        default_vat_rate: parseFloat(form.default_vat_rate) || 0,
        invoice_basis: form.invoice_basis,
        sales_price_basis: form.sales_price_basis,
        postmix_conversion_factor_ml: parseFloat(form.postmix_conversion_factor_ml) || 1000,
      });
      toast({ title: 'Saved', description: 'Stocktaking settings updated successfully' });
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to save settings'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCostingPolicy = async () => {
    setSavingPolicy(true);
    try {
      const result = await costingPolicyService.setGlobal(costingPolicy);
      setCostingPolicy({
        costing_method: result.costing_method,
        allow_reference_cost_fallback: result.allow_reference_cost_fallback,
      });
      toast({ title: 'Saved', description: 'Default costing method updated successfully' });
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to save costing policy'),
        variant: 'destructive',
      });
    } finally {
      setSavingPolicy(false);
    }
  };

  const activeMethod = methods.find(m => m.value === costingPolicy.costing_method);

  return (
    <div className="space-y-6" data-testid="stocktaking-org-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')}
            data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Settings className="h-6 w-6 text-slate-600" />
              Stocktaking Settings
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Organisation-level defaults for costing, VAT and invoice handling
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Phase 4 — Headline Methodology Toggle */}
          <Card data-testid="headline-methodology-card" className="border-rose-200">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FlaskConical className="h-4 w-4 text-rose-600" />
                    Stocktake Headline Methodology
                    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">UAT rollout</Badge>
                  </CardTitle>
                  <CardDescription>
                    Choose how the overall Result is calculated on every stock audit.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button type="button"
                  onClick={() => !savingMethodology && methodology !== 'legacy' && handleSaveMethodology('legacy')}
                  disabled={savingMethodology}
                  className={`text-left rounded-lg border-2 p-4 transition-colors ${methodology === 'legacy' ? 'border-rose-500 bg-rose-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                  data-testid="methodology-legacy-btn">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold">Legacy (PLU-led)</span>
                    {methodology === 'legacy' && <Badge className="text-[10px] bg-rose-600">Active</Badge>}
                  </div>
                  <p className="text-xs text-slate-600 leading-snug">
                    Result = Actual Closing − Expected Closing. Uses PLU Sales Mix to derive expected stock.
                  </p>
                </button>
                <button type="button"
                  onClick={() => !savingMethodology && methodology !== 'revised' && handleSaveMethodology('revised')}
                  disabled={savingMethodology}
                  className={`text-left rounded-lg border-2 p-4 transition-colors ${methodology === 'revised' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                  data-testid="methodology-revised-btn">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold">Actual Receipts methodology</span>
                    {methodology === 'revised' && <Badge className="text-[10px] bg-emerald-600">Active</Badge>}
                  </div>
                  <p className="text-xs text-slate-600 leading-snug">
                    Result = Actual Receipts excl VAT − Estimated Receipts excl VAT. PLU becomes diagnostic only.
                    GP is re-expressed against Actual Receipts.
                  </p>
                </button>
              </div>
              <div className="flex items-start gap-1.5 text-xs text-slate-600 bg-slate-50 rounded p-2">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-500" />
                <span>
                  Sessions fall back to Legacy automatically when Actual Receipts hasn&apos;t been entered, for cellar-only child
                  sessions, and for valuation-only sessions. Recalculate sessions after changing this setting.
                </span>
              </div>
              {savingMethodology && (
                <p className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</p>
              )}
            </CardContent>
          </Card>

          {/* Costing Policy Card */}
          <Card data-testid="costing-policy-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Scale className="h-4 w-4 text-blue-600" />
                    Default Costing Method
                  </CardTitle>
                  <CardDescription>
                    Organisation-wide default for stock valuation and cost of sales calculations.
                    Sites and groups may override this.
                  </CardDescription>
                </div>
                <Button
                  onClick={handleSaveCostingPolicy}
                  disabled={savingPolicy}
                  size="sm"
                  data-testid="save-costing-policy-btn"
                >
                  {savingPolicy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Costing Method</Label>
                  <Select
                    value={costingPolicy.costing_method}
                    onValueChange={(v) => setCostingPolicy(p => ({ ...p, costing_method: v }))}
                  >
                    <SelectTrigger data-testid="costing-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {methods.map(m => (
                        <SelectItem key={m.value} value={m.value}>
                          <div className="flex items-center gap-2">
                            {m.full_label}
                            {!m.available && (
                              <Badge variant="outline" className="text-[10px] py-0 ml-1 text-amber-600 border-amber-300">Pending</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {activeMethod && (
                    <p className="text-xs text-muted-foreground">{activeMethod.description}</p>
                  )}
                  {activeMethod && !activeMethod.available && (
                    <div className="flex items-start gap-1.5 text-xs text-amber-600 bg-amber-50 rounded p-2">
                      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>
                        {activeMethod.label} is registered as a policy option but its cost layer engine is not yet live.
                        The system will fall back to WAC calculations until the engine is fully operational.
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Reference Cost Fallback</Label>
                  <div className="flex items-center gap-3 pt-1">
                    <Switch
                      checked={costingPolicy.allow_reference_cost_fallback}
                      onCheckedChange={(v) => setCostingPolicy(p => ({ ...p, allow_reference_cost_fallback: v }))}
                      data-testid="allow-reference-fallback-switch"
                    />
                    <span className="text-sm">
                      {costingPolicy.allow_reference_cost_fallback
                        ? 'Enabled — product master cost used as last-resort fallback'
                        : 'Disabled — products without site-level cost data flagged as missing'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, the product&apos;s reference cost from the master record is used only after
                    all site-level cost sources are exhausted. This is never used as the authoritative cost.
                  </p>
                </div>
              </div>

              {/* Precedence explanation */}
              <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-2">
                <p className="font-medium text-slate-700">Cost Resolution Precedence</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-600">
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-600 mt-px">1</span>
                    <div>
                      <p className="font-medium">Site Override</p>
                      <p>Site-specific costing method, if set</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-600 mt-px">2</span>
                    <div>
                      <p className="font-medium">Group Override</p>
                      <p>Client group costing method, if set</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-600 mt-px">3</span>
                    <div>
                      <p className="font-medium">Organisation Default</p>
                      <p>This setting (applies to all sites without overrides)</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* VAT, Invoice & Postmix Settings card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">VAT, Invoice & Postmix Configuration</CardTitle>
                  <CardDescription>
                    These defaults apply to all new deliveries and credits across the organisation.
                  </CardDescription>
                </div>
                <Button onClick={handleSave} disabled={saving || loading} size="sm" data-testid="save-org-settings-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="vat-rate">Default VAT Rate (%)</Label>
                  <Input
                    id="vat-rate"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.default_vat_rate}
                    onChange={(e) => setForm(f => ({ ...f, default_vat_rate: e.target.value }))}
                    data-testid="vat-rate-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Standard rate used for VAT calculations on deliveries and credits.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Invoice Basis</Label>
                  <Select value={form.invoice_basis} onValueChange={(v) => setForm(f => ({ ...f, invoice_basis: v }))}>
                    <SelectTrigger data-testid="invoice-basis-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="net">Net (excl. VAT)</SelectItem>
                      <SelectItem value="gross">Gross (incl. VAT)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {form.invoice_basis === 'net'
                      ? 'Line costs entered are exclusive of VAT. VAT is added on top for reconciliation.'
                      : 'Line costs entered are inclusive of VAT. VAT is extracted for reconciliation.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Sales Price Input Basis</Label>
                  <Select value={form.sales_price_basis} onValueChange={(v) => setForm(f => ({ ...f, sales_price_basis: v }))}>
                    <SelectTrigger data-testid="sales-price-basis-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gross">Incl. VAT (selling prices include VAT)</SelectItem>
                      <SelectItem value="net">Excl. VAT (selling prices exclude VAT)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {form.sales_price_basis === 'gross'
                      ? 'Selling prices entered in Sales sheets include VAT. Net will be calculated automatically.'
                      : 'Selling prices entered in Sales sheets are net of VAT. VAT will be added separately.'}
                  </p>
                </div>
              </div>

              {/* Postmix Conversion Factor */}
              <div className="border-t pt-5">
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  Postmix Yield Reference Volume
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="postmix-conv-factor">Conversion Factor (ml)</Label>
                    <Input
                      id="postmix-conv-factor"
                      type="number"
                      step="1"
                      min="1"
                      value={form.postmix_conversion_factor_ml}
                      onChange={(e) => setForm(f => ({ ...f, postmix_conversion_factor_ml: e.target.value }))}
                      data-testid="postmix-conversion-factor-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Sets the reference volume for postmix yield in Product Master. Default 1000ml = per 1L of concentrate.
                      E.g. set to 500 if your suppliers quote dilution ratios per 500ml.
                    </p>
                  </div>
                  <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 space-y-1">
                    <p className="font-medium">How this works</p>
                    <p>
                      In the Product Master, postmix yields are expressed as &quot;X litres of finished drink
                      per {form.postmix_conversion_factor_ml
                        ? (parseFloat(form.postmix_conversion_factor_ml) / 1000).toFixed(
                            parseFloat(form.postmix_conversion_factor_ml) % 1000 === 0 ? 0 : 2
                          )
                        : 1}L of concentrate&quot;.
                    </p>
                    <p>
                      The purchase unit size is handled automatically — you only need to know the
                      dilution ratio.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
